/**
 * Wrapper-chain editor — sysadmin Models → brand detail.
 *
 * Makes the inlet/outlet wrapper-chain composition (argus_model_function_chains)
 * editable config-as-data. Previously the chain was seed-only + read-only. This
 * card adds: "Add function" (pick a published system function + stage + required
 * + on-failure), per-row Edit (position / required / on-failure), and Remove.
 * Backed by the chain CRUD endpoints under /admin/platform/argus-models/:id/chains.
 */

import { useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  useWrapperFunctions,
  useAddModelChainEntry,
  useUpdateModelChainEntry,
  useDeleteModelChainEntry,
  type ChainEntry,
} from '@/lib/use-argus-models';

export function WrapperChainEditor({
  brandId,
  chains,
}: {
  brandId: string;
  chains: ChainEntry[];
}) {
  const fnsQuery = useWrapperFunctions({ status: 'published' });
  const addMut = useAddModelChainEntry(brandId);
  const updateMut = useUpdateModelChainEntry(brandId);
  const deleteMut = useDeleteModelChainEntry(brandId);

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ChainEntry | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ChainEntry | null>(null);

  // add form
  const [fnId, setFnId] = useState('');
  const [stage, setStage] = useState<'inlet' | 'outlet'>('inlet');
  const [required, setRequired] = useState<'mandatory' | 'toggleable'>('mandatory');
  const [onFailure, setOnFailure] = useState<'terminal' | 'warn'>('terminal');

  // edit form
  const [editRequired, setEditRequired] = useState<'mandatory' | 'toggleable'>('mandatory');
  const [editFailure, setEditFailure] = useState<'terminal' | 'warn'>('terminal');
  const [editPosition, setEditPosition] = useState('');

  const fns = fnsQuery.data?.items ?? [];

  function resetAdd() {
    setFnId('');
    setStage('inlet');
    setRequired('mandatory');
    setOnFailure('terminal');
  }

  async function submitAdd() {
    if (!fnId) {
      toast.error('Pick a function first');
      return;
    }
    try {
      await addMut.mutateAsync({
        wrapperFunctionId: fnId,
        chainKind: stage,
        isMandatory: required === 'mandatory',
        failurePolicy: onFailure,
      });
      toast.success('Function added to the chain');
      setAddOpen(false);
      resetAdd();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to add function');
    }
  }

  function openEdit(c: ChainEntry) {
    setEditTarget(c);
    setEditRequired(c.isMandatory ? 'mandatory' : 'toggleable');
    setEditFailure(c.failurePolicy);
    setEditPosition(String(c.position));
  }

  async function submitEdit() {
    if (!editTarget) return;
    const pos = Number(editPosition);
    if (!Number.isInteger(pos) || pos < 1) {
      toast.error('Position must be a positive whole number');
      return;
    }
    try {
      await updateMut.mutateAsync({
        chainId: editTarget.id,
        body: {
          isMandatory: editRequired === 'mandatory',
          failurePolicy: editFailure,
          position: pos,
        },
      });
      toast.success('Chain entry updated');
      setEditTarget(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update entry');
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    try {
      await deleteMut.mutateAsync(removeTarget.id);
      toast.success('Removed from the chain');
      setRemoveTarget(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to remove entry');
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle className="text-base">Wrapper chain</CardTitle>
          <CardDescription>
            The composition of inlet + outlet filters that wrap every request to
            this brand. Mandatory filters always run; toggleable filters run only
            when the tenant has the corresponding Trust toggle enabled.
          </CardDescription>
        </div>
        <Button size="sm" className="gap-1.5 shrink-0" onClick={() => setAddOpen(true)}>
          <Plus className="h-3.5 w-3.5" /> Add function
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Stage</TableHead>
              <TableHead className="text-right w-16">Pos</TableHead>
              <TableHead>Function</TableHead>
              <TableHead>Required</TableHead>
              <TableHead>On failure</TableHead>
              <TableHead className="text-right w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {chains.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                  No functions wired into this chain yet. Click <span className="font-medium">Add function</span> to compose it.
                </TableCell>
              </TableRow>
            )}
            {chains.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px]',
                      c.chainKind === 'inlet'
                        ? 'border-blue-300 text-blue-700'
                        : 'border-purple-300 text-purple-700',
                    )}
                  >
                    {c.chainKind}
                  </Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums text-xs">{c.position}</TableCell>
                <TableCell>
                  <span className="font-medium">{c.wrapperFunctionDisplayName}</span>
                  <div className="text-xs text-muted-foreground font-mono mt-0.5">
                    {c.wrapperFunctionKey}
                  </div>
                </TableCell>
                <TableCell>
                  {c.isMandatory ? (
                    <Badge variant="default" className="text-[10px]">mandatory</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px]">toggleable</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[10px]',
                      c.failurePolicy === 'terminal'
                        ? 'border-red-300 text-red-700'
                        : 'border-amber-300 text-amber-700',
                    )}
                  >
                    {c.failurePolicy}
                  </Badge>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit entry" onClick={() => openEdit(c)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title="Remove from chain" onClick={() => setRemoveTarget(c)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) resetAdd(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add function to chain</DialogTitle>
            <DialogDescription>
              Wire a published system function into this brand's inlet or outlet
              chain. Position auto-appends to the end of the chosen stage.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1">
              <label className="text-xs font-medium">Function</label>
              <Select value={fnId} onValueChange={setFnId}>
                <SelectTrigger><SelectValue placeholder="Pick a published function…" /></SelectTrigger>
                <SelectContent>
                  {fns.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No published functions available</div>}
                  {fns.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.displayName} <span className="text-muted-foreground font-mono">({f.functionKey})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Stage</label>
                <Select value={stage} onValueChange={(v) => setStage(v as 'inlet' | 'outlet')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inlet">inlet</SelectItem>
                    <SelectItem value="outlet">outlet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Required</label>
                <Select value={required} onValueChange={(v) => setRequired(v as 'mandatory' | 'toggleable')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mandatory">mandatory</SelectItem>
                    <SelectItem value="toggleable">toggleable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">On failure</label>
                <Select value={onFailure} onValueChange={(v) => setOnFailure(v as 'terminal' | 'warn')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="terminal">terminal</SelectItem>
                    <SelectItem value="warn">warn</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={submitAdd} disabled={addMut.isPending}>
              {addMut.isPending ? 'Adding…' : 'Add to chain'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit chain entry</DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {editTarget?.wrapperFunctionKey} · {editTarget?.chainKind}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 py-1">
            <div className="space-y-1">
              <label className="text-xs font-medium">Position</label>
              <Input value={editPosition} onChange={(e) => setEditPosition(e.target.value)} inputMode="numeric" />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Required</label>
              <Select value={editRequired} onValueChange={(v) => setEditRequired(v as 'mandatory' | 'toggleable')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mandatory">mandatory</SelectItem>
                  <SelectItem value="toggleable">toggleable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">On failure</label>
              <Select value={editFailure} onValueChange={(v) => setEditFailure(v as 'terminal' | 'warn')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="terminal">terminal</SelectItem>
                  <SelectItem value="warn">warn</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={submitEdit} disabled={updateMut.isPending}>
              {updateMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirm */}
      <Dialog open={!!removeTarget} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove from chain?</DialogTitle>
            <DialogDescription>
              <span className="font-mono">{removeTarget?.wrapperFunctionKey}</span> will no longer wrap requests to this brand on the <span className="font-medium">{removeTarget?.chainKind}</span> stage. This does not delete the function itself.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmRemove} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
